import AppPageTitle from '@/components/AppPageTitle';
import AppTable from '@/components/AppTable';
import AppTableQuery from '@/components/AppTableQuery';
import PATH from '@/config/paths/PATH';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useTopic from '@/redux/hooks/useTopic';
import { exportToExcel } from '@/utils';
import { formatDate, columnSorter, formatNumber } from '@/utils/formatters';
import { Space } from 'antd';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import axiosClient from '@/api/axiosClient';
import { API_GET_TOPICS } from '@/config/paths/API_PATH';
import dayjs from 'dayjs';
import { getReportOpenBox } from '@/services/Box.service';
import _ from 'lodash';

const filterOptions = [
  {
    value: null,
    label: 'All',
  },
  {
    value: '1',
    label: 'Nomal Box',
  },
  {
    value: '2',
    label: 'Monthly Box',
  },
];

const columns = [
  {
    title: 'No.',
    dataIndex: 'no',
    key: 'no',
    sorter: columnSorter('id', 'number'),
    render: (value) => (value ? value : '-'),
  },
  {
    title: 'Isdn',
    dataIndex: 'isdn',
    key: 'isdn',
    render: (value) => {
      return value ? value : '-';
    },
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    render: (value) => (value == 1 ? 'Nomal Box' : 'Monthly Box'),
  },
  {
    title: 'Reward',
    dataIndex: 'reward_value',
    key: 'reward_value',
    sorter: columnSorter('reward_value'),
    render: (value) => (value ? formatNumber(value) : '-'),
  },
  {
    title: 'Award Type',
    dataIndex: 'award_type',
    key: 'award_type',
    sorter: columnSorter('award_type'),
    render: (value) => {
      if (value == null) return '-';
      return value == 1 ? 'Heart' : 'Money';
    },
  },
  {
    title: 'Description',
    dataIndex: 'description',
    key: 'description',
    sorter: columnSorter('description'),
    render: (value) => (value ? value : '-'),
  },
  {
    title: 'Created At',
    dataIndex: 'created_at',
    key: 'created_at',
    render: (date) => (date ? date : '-'),
    sorter: columnSorter('created_at', 'date'),
  },
];
const START_DATE = dayjs().startOf('month').format('YYYY-MM-DD');
const END_DATE = dayjs().format('YYYY-MM-DD');

function OpenBox() {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [queryValues, setQueryValues] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(1);
  const offset = (pageIndex - 1) * pageSize + 1;
  const { setEditingTopicValues } = useTopic();
  const [time, setTime] = useState([START_DATE, END_DATE]);

  const {
    data: dataSource,
    isLoading,
    refetch,
    isRefetching,
  } = usePaginatedQuery(
    () =>
      getReportOpenBox({
        pageSize,
        offset,
        isdn: queryValues?.searchValue,
        boxType: queryValues?.selectedFilter,
        startDate: time[0],
        endDate: time[1],
      }),
    ['topics', pageSize, offset, queryValues]
  );

  const handleQuery = () => {
    setQueryValues({
      searchValue,
      selectedFilter,
      start_date: time[0],
      end_date: time[1],
    });
    setPageIndex(1);
  };

  const handleReset = () => {
    setSearchValue(null);
    setSelectedFilter(null);
    setQueryValues(null);
    setPageIndex(1);
    setTime([START_DATE, END_DATE]);
  };

  const handleEditTopic = (record) => {
    setEditingTopicValues(record);
    navigate(PATH.TOPIC.UPDATE.replace(':id', record.id));
  };

  const handleOnRefresh = () => {
    refetch();
  };

  const dataTable = useMemo(() => {
    if (!dataSource?.data || _.isEmpty(dataSource?.data)) return [];

    return dataSource?.data.map((item, index) => ({
      ...item,
      no: offset + index,
      key: item.id,
      id: item.id,
      name_en: item.name_en,
      name_my: item.name_my,
      is_active: item.is_active === 1 ? 'Active' : 'Inactive',
      created_at: formatDate(item.created_at),
      isdn: item.isdn,
    }));
  }, [dataSource?.data, offset]);

  return (
    <Space direction="vertical" className="w-full">
      <Space className="flex justify-between items-center">
        <AppPageTitle title="Open Box" />
      </Space>
      <AppTableQuery
        timeLabel="Period"
        time={time}
        setTime={setTime}
        filterOptions={filterOptions}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        filterPlaceholder="Box Type"
        searchPlaceholder="Search Phone Number"
        filterLabel="Box Type"
        handleReset={handleReset}
        handleQuery={handleQuery}
        loading={isLoading || isRefetching}
        hasFilter={true}
        rangePicker
        hasDatePicker={true}
      />
      <AppTable
        columns={columns}
        hasRowAction={false}
        dataSource={dataTable || []}
        totalItems={dataSource?.total}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPageChange={(page, size) => {
          setPageIndex(page);
        }}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
        }}
        loading={isLoading || isRefetching}
        onRefresh={handleOnRefresh}
        hasImport
      />
    </Space>
  );
}

export default OpenBox;
